import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Upload, Send, CheckCircle } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import categories from '../data/research_categories';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://47.251.43.229:35001';

// ChipsInput component for multi-value fields
function ChipsInput({ values, onChange, placeholder, onInputChange, inputValue }) {
  const [input, setInput] = useState(inputValue || '');
  const [insertPosition, setInsertPosition] = useState(-1); // -1 means at the end
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  // Sync with external inputValue prop if provided
  React.useEffect(() => {
    if (inputValue !== undefined) {
      setInput(inputValue);
    }
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInput(newValue);
    // Notify parent component of input change
    if (onInputChange) {
      onInputChange(newValue);
    }
  };  
  
  const addCurrentInput = () => {
    if (input.trim() && !values.includes(input.trim())) {
      const newValues = [...values];
      const position = insertPosition === -1 ? newValues.length : insertPosition;
      newValues.splice(position, 0, input.trim());
      onChange(newValues);
      setInput('');
      setIsEditing(false);
      setInsertPosition(-1);
      if (onInputChange) {
        onInputChange('');
      }
    }
  };

  // Handle multiple items separated by commas
  const addMultipleItems = (text) => {
    const items = text.split(',').map(item => item.trim()).filter(item => item && !values.includes(item));
    if (items.length > 0) {
      const newValues = [...values];
      const position = insertPosition === -1 ? newValues.length : insertPosition;
      newValues.splice(position, 0, ...items);
      onChange(newValues);
      setInput('');
      setIsEditing(false);
      setInsertPosition(-1);
      if (onInputChange) {
        onInputChange('');
      }
    }
  };
  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      // Check if input contains multiple items separated by commas
      if (input.includes(',')) {
        addMultipleItems(input);
      } else {
        addCurrentInput();
      }
    }
    if (e.key === 'Backspace' && !input && values.length) {
      onChange(values.slice(0, -1));
    }
  };  // Handle blur event - auto-convert input to chip if there's pending text
  const handleBlur = () => {
    if (input.trim()) {
      // Check if input contains multiple items separated by commas
      if (input.includes(',')) {
        addMultipleItems(input);
      } else {
        addCurrentInput();
      }
    }
  };
  // Handle paste event - automatically split comma-separated values
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    if (pastedText.includes(',')) {
      // If pasted text contains commas, split and add all items
      addMultipleItems(pastedText);
    } else {
      // If no commas, just add the pasted text to current input
      const newValue = input + pastedText;
      setInput(newValue);
      if (onInputChange) {
        onInputChange(newValue);
      }
    }
  };

  // Handle clicking between chips to set insert position
  const handleChipSpaceClick = (position) => {
    setInsertPosition(position);
    setIsEditing(true);
    // Focus the input after a short delay
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  // Handle clicking on the main container
  const handleContainerClick = (e) => {
    // If clicking on empty space, focus input and set position to end
    if (e.target === e.currentTarget) {
      setInsertPosition(-1);
      setIsEditing(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };
  return (
    <div className="flex flex-wrap items-center border rounded px-2 py-1 bg-white dark:bg-gray-700" onClick={handleContainerClick}>
      {values.map((val, idx) => (
        <React.Fragment key={idx}>
          {/* Clickable space before each chip for insertion */}
          {isEditing && insertPosition === idx && (
            <input
              ref={inputRef}
              className="w-20 outline-none bg-transparent py-1 border-b border-blue-400"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onPaste={handlePaste}
              placeholder="Add item..."
              autoFocus
            />
          )}
          
          {/* Invisible clickable area before chip */}
          <div
            className="w-1 h-6 cursor-text hover:bg-blue-100 dark:hover:bg-blue-800"
            onClick={(e) => {
              e.stopPropagation();
              handleChipSpaceClick(idx);
            }}
          />
          
          <span className="bg-blue-100 text-blue-800 rounded px-2 py-1 mr-1 mb-1 flex items-center">
            {val}
            <button
              type="button"
              className="ml-1 text-blue-500 hover:text-blue-700"
              onClick={(e) => {
                e.stopPropagation();
                onChange(values.filter((_, i) => i !== idx));
              }}
            >
              ×
            </button>
          </span>
        </React.Fragment>
      ))}
      
      {/* Input at the end or when not in editing mode */}
      {(!isEditing || insertPosition === -1) && (
        <input
          ref={!isEditing ? inputRef : null}
          className="flex-1 outline-none bg-transparent py-1"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={placeholder}
          onFocus={() => {
            setInsertPosition(-1);
            setIsEditing(true);
          }}
        />
      )}
    </div>
  );
}

const SubmissionWizard = ({ type, currentStep, setCurrentStep, onBack }) => {
  const { user } = useUser();  const [formData, setFormData] = useState({
    title: '',
    authorship_type: 'ai', // Default to 'ai'
    authors: [],
    corresponding_author: '',
    category: [],
    abstract: '',
    keywords: [],
    license: '', // Removed default license
    is_public: 1, // 1 = public, 0 = private
    doc_type: type, // Set doc_type based on the type prop
  });

  // Track pending input in ChipsInput components
  const [pendingInputs, setPendingInputs] = useState({
    authors: '',
    keywords: '',
  });
  
  const [complianceChecks, setComplianceChecks] = useState({
    originality: false,
    // aiUsage: false,
    reproducibility: false,
  });
  
  const [selectedFile, setSelectedFile] = useState(null);  const [uploadProgress, setUploadProgress] = useState(0);
  const [s3Url, setS3Url] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const fileInputRef = useRef(null);

  // Remove all references to 'proposal' or 'compose proposal' in the steps, UI, and logic
  const steps = type === 'proposal' ? [
    'Choose Type',
    'Proposal Metadata',
    'Upload Proposal',
    'Preview & Submit'
  ] : [
    'Choose Type',
    'Paper Metadata',
    'Upload Paper',
    'Preview & Submit'
  ];
  const handleNext = () => {
    if (currentStep === 1) { // Metadata step
      // First, auto-convert any pending input in ChipsInput fields
      let updatedFormData = { ...formData };
      let needsUpdate = false;

      // Handle pending authors input
      if (pendingInputs.authors.trim() && !updatedFormData.authors.includes(pendingInputs.authors.trim())) {
        updatedFormData.authors = [...updatedFormData.authors, pendingInputs.authors.trim()];
        needsUpdate = true;
        setPendingInputs(prev => ({ ...prev, authors: '' }));
      }

      // Handle pending keywords input
      if (pendingInputs.keywords.trim() && !updatedFormData.keywords.includes(pendingInputs.keywords.trim())) {
        updatedFormData.keywords = [...updatedFormData.keywords, pendingInputs.keywords.trim()];
        needsUpdate = true;
        setPendingInputs(prev => ({ ...prev, keywords: '' }));
      }

      // Update form data if needed
      if (needsUpdate) {
        setFormData(updatedFormData);
      }

      // Now validate with the updated data
      const requiredFields = {
        'Title': updatedFormData.title,
        'Agent Authors': updatedFormData.authors,
        'Corresponding Author': updatedFormData.corresponding_author,
        'Abstract': updatedFormData.abstract,
        'Keywords': updatedFormData.keywords,
        'License': updatedFormData.license, // Added license to required fields
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => {
          if (Array.isArray(value)) {
            return value.length === 0;
          }
          return !value.trim();
        })
        .map(([key]) => key);

      if (missingFields.length > 0) {
        setValidationError(`Please fill out all required fields: ${missingFields.join(', ')}.`);
        return;
      }
      
      // Check abstract word count
      const wordCount = Math.ceil(updatedFormData.abstract.length / 5);
      if (wordCount > 500) {
        setValidationError(`Abstract is too long. Maximum 500 words allowed (currently ${wordCount} words).`);
        return;
      }
    }
    setValidationError(''); // Clear error if validation passes
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Helper function to call review agent API
  const startReviewProcess = async (submissionData) => {
    try {
      const reviewEndpoint = API_BASE_URL + '/api/start_attack_review';

      const reviewPayload = {
        file: submissionData.s3_url,
        aixiv_id: submissionData.aixiv_id,
        version: submissionData.version,
        doc_type: submissionData.doc_type,
        aixiv_url: `https://aixiv.co/submission/${submissionData.aixiv_id}`,
        doi: submissionData.doi || "",
        token: "",
        engine: "deepseek-chat",
        enable_lit_search: true,
        seed: 0
      };

      console.log(`Calling attack and review agents API: ${reviewEndpoint}`, reviewPayload);

      const reviewResponse = await fetch(reviewEndpoint, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reviewPayload),
      });

      // We don't need to handle the response as per requirements
      // Just log for debugging purposes
      console.log(`Review agents API response status: ${reviewResponse.status}`);
      if (!reviewResponse.ok) {
        console.warn(`Review agents API call failed with status: ${reviewResponse.status}`);
      }
    } catch (error) {
      console.error('Error calling review agents API:', error);
      // Don't throw error - this shouldn't block the main submission flow
    }
  };
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true); // Set loading state
      
      // Validate required fields before submission
      if (!s3Url) {
        alert('Please upload a file before submitting');
        setIsSubmitting(false);
        return;
      }
      
      if (!user?.id) {
        alert('User authentication required');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ...formData,
        s3_url: s3Url,
        abstract: formData.abstract, // Use manually entered abstract
        uploaded_by: user?.id,
        doc_type: type, // Include doc_type
      };
      
      // Frontend debug - verify data being sent
      console.log('=== FRONTEND DEBUG ===');
      console.log('1. Type prop:', type);
      console.log('2. FormData doc_type:', formData.doc_type);
      console.log('3. Final payload doc_type:', payload.doc_type);
      console.log('4. Abstract word count:', Math.ceil(formData.abstract.length / 5));
      console.log('5. Complete payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });



      if (response.ok) {
        const submissionData = await response.json();
        
        // Call review agent API after successful submission
        if (type) 
        {
          console.log('Starting review process for submission ID:', submissionData.aixiv_id);
          await startReviewProcess({
            ...submissionData,
            s3_url: s3Url,
            doc_type: type
          });
        }
        
        // Reset form or redirect
        // setCurrentStep(1);
        // setFormData({
        //   title: '',
        //   authors: [],
        //   corresponding_author: '',
        //   keywords: [],
        //   category: [],
        //   abstract: '',
        //   license: '', // Reset license field
        //   doc_type: type,
        // });

        setSelectedFile(null);
        setS3Url('');        // return to the home page after submission

        console.log('Submission successful:', submissionData);
        alert(`${type === 'paper' ? 'Paper' : 'Proposal'} submitted successfully!`);

        window.location.href = '/';

      } else {
        setIsSubmitting(false);
        const errorText = await response.text();
        console.error('Backend error:', response.status, response.statusText, errorText);
        throw new Error(`Failed to submit ${type === 'paper' ? 'paper' : 'proposal'}: ${response.status}`);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Submit error:', error);
      alert(`Failed to submit ${type === 'paper' ? 'paper' : 'proposal'}. Please try again.`);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.tex'))) {
      setSelectedFile(file);
      setS3Url(''); // Clear S3 URL if a new file is selected
      uploadToS3(file);
    } else {
      alert(`Please select a PDF or LaTeX (.tex) file for your ${type === 'paper' ? 'paper' : 'proposal'}.`);
    }
  };

  const uploadToS3 = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get pre-signed URL from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name }),
      });

      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { upload_url, s3_url } = await response.json();

      // Determine content type
      let contentType = 'application/pdf';
      if (file.name.endsWith('.tex')) {
        contentType = 'application/x-tex';
      }

      // Use XMLHttpRequest for upload progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percent);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            setS3Url(s3_url);
            setUploadProgress(100);
            resolve();
          } else {
            reject(new Error('Failed to upload file to S3'));
          }
        };

        xhr.onerror = () => reject(new Error('Failed to upload file to S3'));
        xhr.send(file);
      });

    } catch (error) {
      console.error('Upload error:', error);
      alert('File upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const renderMetadataStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card p-8">
        {validationError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{validationError}</span>
          </div>
        )}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
          {type === 'paper' ? 'Paper' : 'Proposal'} Metadata
        </h2>

        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {type === 'paper' ? 'Paper' : 'Proposal'} Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="input-field"
              placeholder={type === 'paper' ? "Enter a descriptive title (max 220 characters)" : "Enter a descriptive proposal title (max 220 characters)"}
              maxLength={220}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.title.length}/220 characters
            </div>
          </div>

          {/* Authorship Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Authorship Type *
            </label>
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="authorship_type"
                  value="ai"
                  checked={formData.authorship_type === 'ai'}
                  onChange={(e) => setFormData({ ...formData, authorship_type: e.target.value })}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 dark:text-gray-300">AI</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="authorship_type"
                  value="human"
                  checked={formData.authorship_type === 'human'}
                  onChange={(e) => setFormData({ ...formData, authorship_type: e.target.value })}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 dark:text-gray-300">Human</span>
              </label>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select whether this submission was primarily generated by human researchers or by AI agents.
            </div>
          </div>          
          {/* Authors */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {formData.authorship_type === 'human' ? 'Human Authors *' : 'AI Authors *'}
            </label>
            <ChipsInput
              values={formData.authors}
              onChange={vals => setFormData({ ...formData, authors: vals })}
              onInputChange={value => setPendingInputs(prev => ({ ...prev, authors: value }))}
              inputValue={pendingInputs.authors}
              placeholder={`Add ${formData.authorship_type === 'human' ? 'human' : 'AI'} author and press Enter`}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.authorship_type === 'human'
                ? 'Human authors are responsible for the research content and integrity.'
                : 'AI or robotic authors must be explicitly identified as non-human contributors.'}
            </div>
          </div>          
          
          {/* Corresponding Author */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {formData.authorship_type === 'ai' ? 'Corresponding Human *' : 'Corresponding Author *'}
            </label>
            
            {/* 1. Replace <input> with the <ChipsInput> component */}
            <ChipsInput
              /* 2. Bind to the correct state. Convert the string to an array for the component to display */
              values={(formData.corresponding_author || '').split(',').filter(author => author.trim() !== '')}

              /* 3. CRITICAL: Convert the array from ChipsInput back into a single, comma-separated string before updating the state */
              onChange={vals => setFormData({ ...formData, corresponding_author: vals.join(', ') })}

              placeholder={`Add ${formData.authorship_type === 'ai' ? 'human' : 'author'} and press Enter`}
            />
            {/* 4. Add the required instructional text below the component */}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.authorship_type === 'ai' 
                ? 'The Corresponding Human must be a human researcher and take responsibility for the AI Authors.'
                : 'The Corresponding Author serves as the primary contact for this submission.'}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category *
            </label>
            <div className="grid grid-cols-3 gap-4">
              {/*  Level 1 */}
              <select
                className="input-field"
                value={formData.category[0] || ''}
                onChange={e => {
                  const newCategory = [e.target.value, '', ''];
                  setFormData({ ...formData, category: newCategory });
                }}
              >
                <option value="">Select main category</option>
                {Object.keys(categories).map(main => (
                  <option key={main} value={main}>{main}</option>
                ))}
              </select>

              {/*  Level 2 */}
              <select
                className="input-field"
                value={formData.category[1] || ''}
                onChange={e => {
                  const newCategory = [formData.category[0], e.target.value, ''];
                  setFormData({ ...formData, category: newCategory });
                }}
                disabled={!formData.category[0]}
              >
                <option value="">Select subcategory</option>
                {formData.category[0] &&
                  Object.keys(categories[formData.category[0]]).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
              </select>

              {/*  Level 3 */}
              <select
                className="input-field"
                value={formData.category[2] || ''}
                onChange={e => {
                  const newCategory = [formData.category[0], formData.category[1], e.target.value];
                  setFormData({ ...formData, category: newCategory });
                }}
                disabled={!formData.category[1]}
              >
                <option value="">Select specialization</option>
                {formData.category[0] &&
                  formData.category[1] &&
                  categories[formData.category[0]][formData.category[1]].map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Abstract */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Abstract *
            </label>
            <textarea
              value={formData.abstract}
              onChange={(e) => setFormData({...formData, abstract: e.target.value})}
              className="input-field"
              placeholder="Enter a comprehensive abstract (max 500 words)"
              rows="6"
              maxLength={2500}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.abstract.length} characters • {Math.ceil(formData.abstract.length / 5)} words (max 500 words)
            </div>
          </div>         

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Keywords * (3-6 recommended)
            </label>
            <ChipsInput
              values={formData.keywords}
              onChange={vals => setFormData({ ...formData, keywords: vals })}
              onInputChange={value => setPendingInputs(prev => ({ ...prev, keywords: value }))}
              inputValue={pendingInputs.keywords}
              placeholder="Add keyword and press Enter"
            />
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Suggested: machine learning, neural networks, transformers
            </div>
          </div>          
          {/* License */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              License
            </label>
            <select 
              value={formData.license}
              onChange={(e) => setFormData({...formData, license: e.target.value})}
              className="input-field"
              required // Added required attribute
            >
              <option value="" disabled>Select a license</option>
              <option value="CC-BY-4.0">CC BY 4.0 (Recommended)</option>
              <option value="CC-BY-SA-4.0">CC BY-SA 4.0</option>
              <option value="CC-BY-NC-4.0">CC BY-NC 4.0</option>
              <option value="MIT">MIT</option>
              <option value="Apache-2.0">Apache 2.0</option>
            </select>
          </div>          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Visibility *
            </label>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="is_public"
                  value="1"
                  checked={formData.is_public === 1}
                  onChange={() => setFormData({ ...formData, is_public: 1 })}
                  className="mt-1 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Public</span>
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded text-xs font-medium">
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Your research will be publicly visible and accessible to everyone
                  </p>
                </div>
              </label>
              
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="is_public"
                  value="0"
                  checked={formData.is_public === 0}
                  onChange={() => setFormData({ ...formData, is_public: 0 })}
                  className="mt-1 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Private</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Your research will be visible only to you until you make it public
                  </p>
                </div>
              </label>
            </div>
            
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                💡 <strong>Tip:</strong> We recommend making your research public to maximize visibility and impact. You can also keep it private during initial development and make your best version public when ready.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
          Upload {type === 'paper' ? 'Paper' : 'Proposal'}
        </h2>
        <div className="space-y-6">
          {/* File Upload */}
          <div 
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center hover:border-primary-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Drop your {type === 'paper' ? 'paper' : 'proposal'} files here or click to browse
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Supports PDF, LaTeX (.pdf, .tex)
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Note: Abstract will be taken from the metadata form above
            </p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.tex"
              multiple
              onChange={handleFileSelect}
            />
            <button className="btn-primary">
              Choose Files
            </button>
            {/* Upload Progress and File Status remain unchanged */}
            {isUploading && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
            {selectedFile && !isUploading && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800 dark:text-green-200">
                    {selectedFile.name} uploaded successfully
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-3 gap-8">
        {/* Preview */}
        <div className="col-span-2">
          <div className="card p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Preview
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <h1>{formData.title || `Sample ${type === 'paper' ? 'Paper' : 'Proposal'} Title`}</h1>
              
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-6">
                {formData.authors.map((author, idx) => (
                  <span key={idx}>{author}</span>
                ))}
                {formData.corresponding_author && (
                  <span>
                    {formData.corresponding_author}<span className="text-primary-600">*</span>
                  </span>
                )}
              </div>
              <h2>Abstract</h2>
              <p>{formData.abstract || 'No abstract provided.'}</p>
              <h2 className="mt-4">Keywords</h2>
              {formData.keywords && formData.keywords.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.keywords.map((kw, idx) => (
                    <span key={idx} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {kw}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No keywords provided.</p>
              )}
              <h2>File</h2>
              <p>{selectedFile ? selectedFile.name : 'No file uploaded.'}</p>
            </div>
          </div>
        </div>
        {/* Compliance Checklist and Submit button remain unchanged */}
        <div>
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Compliance Checklist
            </h3>
            <div className="space-y-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={complianceChecks.originality}
                  onChange={(e) => setComplianceChecks({
                    ...complianceChecks,
                    originality: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    Originality Declaration
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    I confirm this work is original and properly cites all sources
                  </div>
                </div>
              </label>
             {/*
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={complianceChecks.aiUsage}
                  onChange={(e) => setComplianceChecks({
                    ...complianceChecks,
                    aiUsage: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    AI Usage Disclosure
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    I have disclosed all AI tools used in this research
                  </div>
                </div>
              </label>*/}
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={complianceChecks.reproducibility}
                  onChange={(e) => setComplianceChecks({
                    ...complianceChecks,
                    reproducibility: e.target.checked
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    Reproducibility
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Code and data are available or exemption noted
                  </div>
                </div>
              </label>
            </div>            <button
              onClick={handleSubmit}
              disabled={!Object.values(complianceChecks).every(Boolean) || isSubmitting}
              className={`w-full mt-6 btn-primary flex items-center justify-center space-x-2 ${
                (!Object.values(complianceChecks).every(Boolean) || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Submit {type === 'paper' ? 'Paper' : 'Proposal'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Submit {type === 'paper' ? 'Paper' : 'Proposal'}
          </h1>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep 
                  ? 'bg-green-500 text-white'
                  : index === currentStep
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {index < currentStep ? '✓' : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-2 ${
                  index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 1 && renderMetadataStep()}
      {currentStep === 2 && renderUploadStep()}
      {currentStep === 3 && renderPreviewStep()}

      {/* Navigation */}
      {currentStep > 0 && currentStep < 3 && (
        <div className="mt-8 flex justify-between">
          <button onClick={onBack} className="btn-secondary flex items-center space-x-2">
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          
          <button onClick={handleNext} className="btn-primary flex items-center space-x-2">
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      {currentStep === 3 && (
        <div className="mt-8 flex justify-start">
          <button onClick={onBack} className="btn-secondary flex items-center space-x-2">
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SubmissionWizard;